/* ──────────────────────────────────────────────
 *  Booking Tunnel V2 — XState State Machine
 * ────────────────────────────────────────────── */

import { assign, setup } from 'xstate';
import { PlaceKind } from '@cfixe-monorepo/shared';
import { cartItemNeedsMember, getEligibleMembersForItem, getRequiredMemberCount } from '../utils/member-eligibility';
import { computeCompatiblePlaceIds } from '../utils/place-compat';
import { groupPlacesByKind, type PlaceGroup } from '../utils/place-groups';
import { isSameCartLine } from '../cart/booking-cart-lines';
import type {
  AddressObject,
  AppliedCodes,
  BookableSlot,
  BookingContext,
  BookingCustomField,
  BookingEntryMode,
  BookingParams,
  BookingPrestation,
  BookingSlotNotice,
  BookingStep,
  CartItem,
  CartSummary,
  Category,
  MemberAssignment,
  PreReservationResponse,
  PrestationBookability,
  PriceOption,
  PublicPlace,
  ResourceAssignment,
  TeamMember,
} from '../types';

/**
 * Reporte le verdict live sur les lignes de prix, PAR DURÉE : l'endpoint léger `/bookability` ne
 * renvoie pas le catalogue, donc pas d'index — la jointure se fait sur `duration_minutes`.
 * Une durée absente de la réponse n'a pas été sondée : son drapeau du chargement initial est
 * conservé tel quel (jamais de masquage sans preuve, jamais de démasquage arbitraire non plus).
 */
const mergeDurationBookability = (
  prices: PriceOption[],
  durations: PrestationBookability['durations'],
): PriceOption[] => {
  if (!durations?.length) return prices;
  const bookableByDuration = new Map(durations.map((d): [number, boolean] => [d.duration_minutes, d.is_bookable]));
  return prices.map((price): PriceOption => {
    const isBookable = bookableByDuration.get(price.duration_minutes);
    return isBookable === undefined ? price : { ...price, is_bookable: isBookable };
  });
};

// ── Events ──────────────────────────────────────

/** Étapes que les liens « modifier » du récap savent rouvrir. */
export type BookingEditableStep = 'services' | 'place' | 'zone_address' | 'slot';

export type BookingEvent =
  | {
      type: 'DATA_LOADED';
      prestations: BookingPrestation[];
      categories: Category[];
      pro_places: PublicPlace[];
      team_members: TeamMember[];
      custom_fields?: BookingCustomField[];
      booking_params?: BookingParams;
    }
  /* `error` : message destiné au client (erreur métier de l'API), absent pour une panne réseau. */
  | { type: 'DATA_ERROR'; error?: string }
  /* Nouvel essai de chargement après une erreur (bouton « Réessayer »). */
  | { type: 'RETRY' }
  /* Catalogue complet arrivé après un premier chargement restreint à la prestation du parcours express. */
  | { type: 'CATALOGUE_LOADED'; prestations: BookingPrestation[]; categories: Category[] }
  /* Mode du parcours résolu à l'ouverture (panier, lien, catalogue). */
  | { type: 'SET_ENTRY_MODE'; mode: BookingEntryMode }
  | { type: 'BOOKABILITY_REFRESHED'; bookability: PrestationBookability[] }
  /* Présélections amont écartées — alimente les bandeaux : formule devenue non réservable (`names`),
     prestation qu'aucun planning ne rend réservable en ligne (`unbookableNames`). */
  | { type: 'PRESELECTIONS_DROPPED'; names: string[]; unbookableNames: string[] }
  | { type: 'ADD_TO_CART'; item: CartItem }
  | { type: 'REPLACE_CART'; item: CartItem }
  /* Sélection reprise du panier unique (ouverture du tunnel, bandeau « Reprendre ») : remplace le panier. */
  | { type: 'RESTORE_CART'; items: CartItem[] }
  | { type: 'REMOVE_FROM_CART'; index: number }
  | { type: 'CART_SUMMARY_LOADING' }
  | { type: 'CART_SUMMARY_LOADED'; summary: CartSummary }
  /* Résumé du panier en échec : le chargement retombe, le récap propose « Réessayer » (bug 11). */
  | { type: 'CART_SUMMARY_FAILED' }
  /* Codes du champ unique posés ou retirés ; `summary` : résumé du panier déjà calculé avec eux. */
  | { type: 'CODES_CHANGED'; codes: AppliedCodes; summary?: CartSummary }
  | { type: 'CONFIRM_SERVICES' }
  /* Écran lieu, ou puces au-dessus du calendrier (parcours express). */
  | { type: 'SELECT_PLACE'; placeId: number }
  | { type: 'SAVE_CUSTOMER_ADDRESS'; address: AddressObject }
  /* Sélecteurs au-dessus du calendrier : chaque choix vide le créneau retenu, la grille se recharge. */
  | { type: 'SET_MEMBERS'; assignments: MemberAssignment[] }
  | { type: 'SET_RESOURCES'; assignments: ResourceAssignment[] }
  | { type: 'SLOTS_LOADING' }
  | { type: 'SLOTS_LOADED'; slots: BookableSlot[] }
  | { type: 'SLOTS_ERROR'; error: string }
  | { type: 'SET_WEEK_START'; weekStart: string }
  | { type: 'SELECT_SLOT'; slot: BookableSlot }
  | { type: 'CONFIRM_SLOT' }
  | { type: 'SAVE_CUSTOMER_INFO'; info: BookingContext['customerInfo'] }
  | { type: 'PRE_RESERVATION_CREATED'; data: PreReservationResponse }
  /* Pré-réservation libérée sans quitter la finalisation (le client modifie ses coordonnées). */
  | { type: 'PRE_RESERVATION_RELEASED' }
  | { type: 'PAYMENT_CONFIRMED'; reservationUuid: string }
  | { type: 'TIMER_EXPIRED' }
  /* Le créneau a été pris pendant la saisie (refus de la pré-réservation). */
  | { type: 'SLOT_TAKEN' }
  /* « Demander un devis » d'une prestation sur devis. */
  | { type: 'REQUEST_QUOTE'; prestationId: number }
  | { type: 'BACK' }
  | { type: 'EDIT_STEP'; step: BookingEditableStep };

// ── Initial context ─────────────────────────────

const initialContext: BookingContext = {
  prestations: [],
  baselinePrestations: [],
  categories: [],
  availablePlaces: [],
  availableMembers: [],
  customFields: [],
  bookingParams: {},
  dataLoading: true,

  entryMode: 'standard',

  cartItems: [],
  cartSummary: undefined,
  cartSummaryLoading: false,
  appliedCodes: {},
  droppedPreselections: [],
  unbookablePreselections: [],

  selectedPlaceId: undefined,

  customerAddress: undefined,

  memberAssignments: [],

  resourceAssignments: [],

  slots: [],
  slotsLoading: false,
  selectedSlot: undefined,
  slotWeekStart: undefined,
  slotNotice: undefined,

  quotePrestationId: undefined,

  customerInfo: undefined,
  preReservationUuid: undefined,
  preReservationData: undefined,
  confirmedReservationUuid: undefined,

  error: undefined,
};

// ── Helpers ─────────────────────────────────────

function compatiblePlaces(context: BookingContext): PublicPlace[] {
  const allIds = context.availablePlaces.map((p): number => p.pro_place_id);
  const compatibleIds = computeCompatiblePlaceIds(context.cartItems, allIds);
  return context.availablePlaces.filter((p): boolean => compatibleIds.has(p.pro_place_id));
}

function getSingleCompatiblePlace(context: BookingContext): PublicPlace | undefined {
  if (context.availablePlaces.length === 0) return undefined;
  const places = compatiblePlaces(context);
  return places.length === 1 ? places[0] : undefined;
}

function isSinglePlace(context: BookingContext): boolean {
  return getSingleCompatiblePlace(context) !== undefined;
}

function placeIsZone(place: PublicPlace | undefined): boolean {
  return place?.kind === PlaceKind.TRAVELING;
}

function selectedPlaceIsZone(context: BookingContext): boolean {
  const place = context.availablePlaces.find((p): boolean => p.pro_place_id === context.selectedPlaceId);
  return placeIsZone(place);
}

function singlePlaceIsZone(context: BookingContext): boolean {
  const place = getSingleCompatiblePlace(context);
  return place !== undefined && placeIsZone(place);
}

/**
 * Lieu proposé d'office au-dessus du calendrier en parcours express : le premier lieu compatible dans
 * l'ordre des groupes (en établissement, à distance), jamais une zone à domicile, qui garde son écran
 * d'adresse. Aucun ⇒ l'écran lieu reste nécessaire.
 */
function getDefaultChipPlace(context: BookingContext): PublicPlace | undefined {
  return groupPlacesByKind(compatiblePlaces(context))
    .flatMap((group): PublicPlace[] => group.places)
    .find((place): boolean => !placeIsZone(place));
}

/* Parcours express : le lieu se choisit au-dessus du calendrier dès qu'un lieu hors domicile convient. */
function usesPlaceChips(context: BookingContext): boolean {
  return context.entryMode === 'express' && !isSinglePlace(context) && getDefaultChipPlace(context) !== undefined;
}

/** Lieux proposés en puces au-dessus du calendrier (parcours express), groupés ; vide hors express. */
export function placeChipGroups(context: BookingContext): PlaceGroup[] {
  return usesPlaceChips(context) ? groupPlacesByKind(compatiblePlaces(context)) : [];
}

/** Plusieurs lieux conviennent au panier : la sortie « Autre lieu » a un sens. */
export function hasOtherPlace(context: BookingContext): boolean {
  return compatiblePlaces(context).length > 1;
}

/* L'étape membre est sautée quand elle n'offre aucun VRAI choix : pour chaque item du panier
   nécessitant un membre, le nombre de membres réellement éligibles (qualifiés `team_member_ids` +
   compatibles lieu) ne dépasse pas le nombre requis. Ex. : coupe réalisable par le seul membre A et
   brushing par le seul membre B ⇒ aucun choix, on auto-assigne A et B et on saute l'étape (le
   critère est par item, pas sur l'union des éligibles du panier). */
export function isMemberChoiceSkippable(context: BookingContext): boolean {
  /* Libre-service everywhere → no member step at all */
  if (context.cartItems.length > 0 && context.cartItems.every((item): boolean => !cartItemNeedsMember(item))) {
    return true;
  }
  if (context.availableMembers.length <= 1) return true;
  return context.cartItems.every((item): boolean => {
    if (!cartItemNeedsMember(item)) return true;
    const eligible = getEligibleMembersForItem(item, context.availableMembers, context.selectedPlaceId);
    return eligible.length <= getRequiredMemberCount(item);
  });
}

function buildAutoMemberAssignments(context: BookingContext): MemberAssignment[] {
  return context.cartItems.map((item, i): MemberAssignment => {
    if (!cartItemNeedsMember(item)) {
      return { prestationId: item.prestation.prestation_id, cartIndex: i, memberId: undefined };
    }
    /* Auto-assignation (aucun vrai choix) : on assigne le membre réellement éligible POUR CET item
       (qualifié + compatible lieu), et non le 1er membre du pro. Si l'item est contraint mais qu'aucun
       membre éligible n'est disponible, on laisse `memberId` indéfini (pas de repli sur un membre non
       qualifié) : la combinaison sera de toute façon non réservable. */
    const eligible = getEligibleMembersForItem(item, context.availableMembers, context.selectedPlaceId);
    const required = getRequiredMemberCount(item);
    if (required >= 2) {
      /* Multi-membres : on fige la liste seulement si elle est exactement complète ; sinon « sans
         préférence » (le backend auto-assigne parmi les membres qualifiés). */
      const memberIds = eligible.length === required ? eligible.map((m): number => m.team_member_id) : undefined;
      return {
        prestationId: item.prestation.prestation_id,
        cartIndex: i,
        memberId: memberIds?.[0],
        memberIds,
      };
    }
    return {
      prestationId: item.prestation.prestation_id,
      cartIndex: i,
      memberId: eligible[0]?.team_member_id,
    };
  });
}

function buildDefaultMemberAssignments(context: BookingContext): MemberAssignment[] {
  return context.cartItems.map(
    (item, i): MemberAssignment => ({
      prestationId: item.prestation.prestation_id,
      cartIndex: i,
      memberId: undefined /* sans préférence (ou libre-service) */,
    }),
  );
}

function getSelectableResourcesForCartItem(
  item: CartItem,
  selectedPlaceId: number | undefined,
): NonNullable<CartItem['prestation']['selectable_resources']> {
  const all = item.prestation.selectable_resources ?? [];
  if (selectedPlaceId === undefined) return all;
  return all.filter(
    (r): boolean => !r.pro_place_ids || r.pro_place_ids.length === 0 || r.pro_place_ids.includes(selectedPlaceId),
  );
}

/**
 * Ressource de chaque article au lieu choisi : celle déjà choisie (fiche du site, choix précédent) si elle y
 * est proposée, sinon la première du lieu, pour que la grille s'ouvre tout de suite. Le client en change
 * au-dessus du calendrier.
 */
function buildInitialResourceAssignments(context: BookingContext): ResourceAssignment[] {
  return context.cartItems.map((item, i): ResourceAssignment => {
    const atPlace = getSelectableResourcesForCartItem(item, context.selectedPlaceId);
    const previous =
      context.resourceAssignments.find((a): boolean => a.cartIndex === i)?.pro_resource_id ?? item.selected_resource_id;
    const kept = atPlace.some((r): boolean => r.pro_resource_id === previous) ? previous : undefined;
    return {
      prestationId: item.prestation.prestation_id,
      cartIndex: i,
      pro_resource_id: kept ?? atPlace[0]?.pro_resource_id,
    };
  });
}

/** Étape montrée au client : la confirmation est le résultat du parcours, pas une étape ; le devis est à part. */
export type VisibleBookingStep = Exclude<BookingStep, 'success' | 'quote'>;

/**
 * Étapes que le client verra, dans l'ordre, calculées avec LES GARDES DES TRANSITIONS : la barre de
 * progression ne peut pas annoncer une étape que la machine sautera (ou l'inverse). Membre et ressource
 * n'ont plus d'écran (au-dessus du calendrier). En express, ni écran prestations ni écran lieu (puces) ;
 * l'adresse ne compte qu'une fois le lieu à domicile connu. Le total peut donc bouger en chemin.
 */
export function visibleSteps(context: BookingContext): VisibleBookingStep[] {
  const steps: VisibleBookingStep[] = context.entryMode === 'express' ? [] : ['services'];
  const singlePlace = getSingleCompatiblePlace(context);
  if (!singlePlace && !(context.entryMode === 'express' && getDefaultChipPlace(context))) steps.push('place');
  if (singlePlace ? placeIsZone(singlePlace) : selectedPlaceIsZone(context)) steps.push('zone_address');
  steps.push('slot', 'checkout');
  return steps;
}

/* Effacements communs. Les coordonnées saisies survivent à tout retour en arrière (spec Parcours § 7.6). */
const PRE_RESERVATION_CLEARED = {
  preReservationUuid: undefined,
  preReservationData: undefined,
  confirmedReservationUuid: undefined,
} satisfies Partial<BookingContext>;

const SLOT_CLEARED = {
  selectedSlot: undefined,
  slots: [],
  slotNotice: undefined,
  ...PRE_RESERVATION_CLEARED,
} satisfies Partial<BookingContext>;

/* Choix faits au-dessus du calendrier, à refaire quand le panier ou le lieu change. */
function slotSelectorsFor(context: BookingContext): Partial<BookingContext> {
  const memberAssignments = isMemberChoiceSkippable(context)
    ? buildAutoMemberAssignments(context)
    : buildDefaultMemberAssignments(context);
  return { memberAssignments, resourceAssignments: buildInitialResourceAssignments(context) };
}

/* Retour à l'écran prestations depuis l'express : le client parcourt le catalogue, le parcours devient standard. */
const leaveExpress = (context: BookingContext): BookingEntryMode =>
  context.entryMode === 'express' ? 'standard' : context.entryMode;

// ── Machine ─────────────────────────────────────

export const bookingMachine = setup({
  types: {
    context: {} as BookingContext,
    events: {} as BookingEvent,
  },
  guards: {
    singlePlace: ({ context }): boolean => isSinglePlace(context),
    singlePlaceIsZone: ({ context }): boolean => singlePlaceIsZone(context),
    placeChips: ({ context }): boolean => usesPlaceChips(context),
    selectedPlaceIsZone: ({ context }): boolean => selectedPlaceIsZone(context),
    eventPlaceIsZone: ({ context, event }): boolean => {
      if (event.type !== 'SELECT_PLACE') return false;
      return placeIsZone(context.availablePlaces.find((p): boolean => p.pro_place_id === event.placeId));
    },
    isExpress: ({ context }): boolean => context.entryMode === 'express',
  },
  actions: {
    assignDataLoaded: assign({
      prestations: ({ event }): BookingPrestation[] => {
        const e = event as Extract<BookingEvent, { type: 'DATA_LOADED' }>;
        return e.prestations;
      },
      /* Même référence que `prestations` : sert de point de restauration quand le panier
         redevient vide (cf. removeFromCart). Seul `mergeBookability` fait diverger les deux. */
      baselinePrestations: ({ event }): BookingPrestation[] => {
        const e = event as Extract<BookingEvent, { type: 'DATA_LOADED' }>;
        return e.prestations;
      },
      categories: ({ event }): Category[] => {
        const e = event as Extract<BookingEvent, { type: 'DATA_LOADED' }>;
        return e.categories;
      },
      availablePlaces: ({ event }): PublicPlace[] => {
        const e = event as Extract<BookingEvent, { type: 'DATA_LOADED' }>;
        return e.pro_places;
      },
      availableMembers: ({ event }): TeamMember[] => {
        const e = event as Extract<BookingEvent, { type: 'DATA_LOADED' }>;
        return e.team_members;
      },
      customFields: ({ event }): BookingCustomField[] => {
        const e = event as Extract<BookingEvent, { type: 'DATA_LOADED' }>;
        return e.custom_fields ?? [];
      },
      bookingParams: ({ event }): BookingParams => {
        const e = event as Extract<BookingEvent, { type: 'DATA_LOADED' }>;
        return e.booking_params ?? {};
      },
      dataLoading: (): boolean => false,
    }),

    /* Le catalogue complet remplace celui de la seule prestation express ; la sélection garde ses articles. */
    assignCatalogue: assign(({ event }): Partial<BookingContext> => {
      const e = event as Extract<BookingEvent, { type: 'CATALOGUE_LOADED' }>;
      return { prestations: e.prestations, baselinePrestations: e.prestations, categories: e.categories };
    }),

    assignEntryMode: assign({
      entryMode: ({ context, event }): BookingEntryMode => {
        const e = event as Extract<BookingEvent, { type: 'SET_ENTRY_MODE' }>;
        return context.cartItems.length >= 2 ? 'multi' : e.mode;
      },
    }),

    leaveExpress: assign({
      entryMode: ({ context }): BookingEntryMode => leaveExpress(context),
    }),

    /* Met à jour la réservabilité (prestation + durées) et `first_bookable_date` (recalcul live)
     * sans toucher au reste des prestations. Fusionne AUSSI la date dans cartItems[].prestation
     * ET cartItems[].selectedPrice : AvailabilityPicker lit `selectedPrice.first_bookable_date ??
     * prestation.first_bookable_date` — sans l'écriture dans selectedPrice, la date par durée de la
     * sonde initiale (hors lieu, minimum sur TOUS les lieux) masquerait à jamais la date re-sondée
     * au lieu choisi (audit P2 #6), et la grille s'ouvrirait sur une semaine vide chez un pro
     * saturé au lieu élu. La date re-sondée est calculée avec la durée réelle du panier : elle est
     * légitime pour les deux champs. */
    mergeBookability: assign({
      prestations: ({ context, event }): BookingPrestation[] => {
        const e = event as Extract<BookingEvent, { type: 'BOOKABILITY_REFRESHED' }>;
        const refreshedByPrestation = new Map(
          e.bookability.map((b): [number, PrestationBookability] => [b.prestation_id, b]),
        );
        return context.prestations.map((p): BookingPrestation => {
          const refreshed = refreshedByPrestation.get(p.prestation_id);
          if (refreshed === undefined) return p;
          return {
            ...p,
            is_bookable: refreshed.is_bookable,
            prices: mergeDurationBookability(p.prices, refreshed.durations),
            ...(refreshed.first_bookable_date !== undefined
              ? { first_bookable_date: refreshed.first_bookable_date }
              : {}),
          };
        });
      },
      cartItems: ({ context, event }): CartItem[] => {
        const e = event as Extract<BookingEvent, { type: 'BOOKABILITY_REFRESHED' }>;
        const refreshedByPrestation = new Map(
          e.bookability.map((b): [number, PrestationBookability] => [b.prestation_id, b]),
        );
        // L'IDENTITÉ du tableau doit être conservée quand rien ne change : l'effet du récapitulatif
        // panier a `cartItems` en dépendance, et un simple `.map()` renvoie un tableau neuf à
        // chaque rafraîchissement de bookability — le total repasserait à « … » et un POST
        // /cart-summary identique serait renvoyé, à chaque cycle.
        let changed = false;
        const next = context.cartItems.map((item): CartItem => {
          const refreshed = refreshedByPrestation.get(item.prestation.prestation_id);
          if (refreshed?.first_bookable_date === undefined) return item;
          if (
            item.prestation.first_bookable_date === refreshed.first_bookable_date &&
            item.selectedPrice.first_bookable_date === refreshed.first_bookable_date
          ) {
            return item;
          }
          changed = true;
          return {
            ...item,
            prestation: { ...item.prestation, first_bookable_date: refreshed.first_bookable_date },
            selectedPrice: { ...item.selectedPrice, first_bookable_date: refreshed.first_bookable_date },
          };
        });
        return changed ? next : context.cartItems;
      },
    }),

    setDroppedPreselections: assign({
      droppedPreselections: ({ event }): string[] => {
        const e = event as Extract<BookingEvent, { type: 'PRESELECTIONS_DROPPED' }>;
        return e.names;
      },
      unbookablePreselections: ({ event }): string[] => {
        const e = event as Extract<BookingEvent, { type: 'PRESELECTIONS_DROPPED' }>;
        return e.unbookableNames;
      },
    }),

    addToCart: assign(({ context, event }): Partial<BookingContext> => {
      const e = event as Extract<BookingEvent, { type: 'ADD_TO_CART' }>;
      /* Multi-prestation OFF: adding replaces the current selection (no dialog, button label says it) */
      if (context.bookingParams.allow_multi_prestation === false) {
        return { cartItems: [e.item] };
      }
      /* Même prestation et même formule : jamais deux lignes (l'enchaînement deviendrait impossible sans
         que le client comprenne pourquoi). La quantité augmente si la prestation l'ouvre, sinon rien ne
         change, la carte est déjà « Choisi ». */
      const existing = context.cartItems.findIndex((item): boolean => isSameCartLine(item, e.item));
      const maxQuantity = Math.max(1, e.item.prestation.max_quantity ?? 1);
      const cartItems =
        existing < 0
          ? [...context.cartItems, e.item]
          : context.cartItems.map(
              (item, index): CartItem =>
                index === existing
                  ? { ...item, quantity: Math.min(maxQuantity, item.quantity + e.item.quantity) }
                  : item,
            );
      /* Deuxième prestation : le parcours devient multi, et le reste même si le client en retire une. */
      return { cartItems, ...(cartItems.length >= 2 ? { entryMode: 'multi' as const } : {}) };
    }),

    restoreCart: assign(({ context, event }): Partial<BookingContext> => {
      const e = event as Extract<BookingEvent, { type: 'RESTORE_CART' }>;
      const cartItems = context.bookingParams.allow_multi_prestation === false ? e.items.slice(0, 1) : e.items;
      return {
        cartItems,
        prestations: context.baselinePrestations,
        ...(cartItems.length >= 2 ? { entryMode: 'multi' as const } : {}),
      };
    }),

    /* Remplace toute la sélection (prestation non combinable avec le panier, ou multi-prestation
       OFF). Les verdicts de réservabilité en place ont été calculés CONTRE l'ancien panier : on
       repart de ceux du chargement initial, que le recalcul live réajuste pour le nouveau panier. */
    replaceCart: assign(({ context, event }): Partial<BookingContext> => {
      const e = event as Extract<BookingEvent, { type: 'REPLACE_CART' }>;
      return { cartItems: [e.item], prestations: context.baselinePrestations };
    }),

    removeFromCart: assign(({ context, event }): Partial<BookingContext> => {
      const e = event as Extract<BookingEvent, { type: 'REMOVE_FROM_CART' }>;
      const cartItems = context.cartItems.filter((_, i): boolean => i !== e.index);
      /* Panier redevenu vide ⇒ restaurer le verdict de réservabilité du chargement initial
         (sondé sans panier, donc exact pour l'état courant). Le recalcul live ne se déclenche
         qu'avec un panier non vide : sans cette restauration, les `is_bookable: false` posés par
         le dernier check (« Incompatible avec votre sélection ») resteraient collés à jamais.
         Garde d'identité : ne remplacer le tableau que si un merge a réellement eu lieu, pour ne
         pas re-rendre le listing inutilement. */
      const prestations =
        cartItems.length === 0 && context.prestations !== context.baselinePrestations
          ? context.baselinePrestations
          : context.prestations;
      return { cartItems, prestations };
    }),

    setCartSummaryLoading: assign({
      cartSummaryLoading: (): boolean => true,
    }),

    assignCartSummary: assign({
      cartSummary: ({ event }): CartSummary | undefined => {
        const e = event as Extract<BookingEvent, { type: 'CART_SUMMARY_LOADED' }>;
        return e.summary;
      },
      cartSummaryLoading: (): boolean => false,
    }),

    clearCartSummary: assign({
      cartSummary: (): CartSummary | undefined => undefined,
      cartSummaryLoading: (): boolean => false,
    }),

    stopCartSummaryLoading: assign({
      cartSummaryLoading: (): boolean => false,
    }),

    assignCodes: assign(({ context, event }): Partial<BookingContext> => {
      const e = event as Extract<BookingEvent, { type: 'CODES_CHANGED' }>;
      return e.summary
        ? { appliedCodes: e.codes, cartSummary: e.summary, cartSummaryLoading: false }
        : { appliedCodes: e.codes, cartSummary: context.cartSummary };
    }),

    autoAssignSinglePlace: assign({
      selectedPlaceId: ({ context }): number | undefined => getSingleCompatiblePlace(context)?.pro_place_id,
    }),

    assignDefaultChipPlace: assign({
      selectedPlaceId: ({ context }): number | undefined => getDefaultChipPlace(context)?.pro_place_id,
      customerAddress: (): AddressObject | undefined => undefined,
    }),

    assignPlace: assign({
      selectedPlaceId: ({ event }): number | undefined => {
        const e = event as Extract<BookingEvent, { type: 'SELECT_PLACE' }>;
        return e.placeId;
      },
      customerAddress: (): AddressObject | undefined => undefined,
    }),

    assignCustomerAddress: assign({
      customerAddress: ({ event }): AddressObject | undefined => {
        const e = event as Extract<BookingEvent, { type: 'SAVE_CUSTOMER_ADDRESS' }>;
        return e.address;
      },
    }),

    clearCustomerAddress: assign({
      customerAddress: (): AddressObject | undefined => undefined,
    }),

    /* Entrée dans l'étape créneau : membres (auto quand il n'y a pas de vrai choix) et ressources par défaut. */
    initSlotSelectors: assign(({ context }): Partial<BookingContext> => slotSelectorsFor(context)),

    assignMembers: assign(({ event }): Partial<BookingContext> => {
      const e = event as Extract<BookingEvent, { type: 'SET_MEMBERS' }>;
      return { memberAssignments: e.assignments, ...SLOT_CLEARED };
    }),

    assignResources: assign(({ event }): Partial<BookingContext> => {
      const e = event as Extract<BookingEvent, { type: 'SET_RESOURCES' }>;
      return { resourceAssignments: e.assignments, ...SLOT_CLEARED };
    }),

    assignSlot: assign({
      selectedSlot: ({ event }): BookableSlot | undefined => {
        const e = event as Extract<BookingEvent, { type: 'SELECT_SLOT' }>;
        return e.slot;
      },
      slotNotice: (): BookingSlotNotice | undefined => undefined,
    }),

    clearSlot: assign({
      selectedSlot: (): BookableSlot | undefined => undefined,
    }),

    clearSlotAndGrid: assign((): Partial<BookingContext> => SLOT_CLEARED),

    setSlotsLoading: assign({
      slotsLoading: (): boolean => true,
    }),

    assignSlots: assign({
      slots: ({ event }): BookableSlot[] => {
        const e = event as Extract<BookingEvent, { type: 'SLOTS_LOADED' }>;
        return e.slots;
      },
      slotsLoading: (): boolean => false,
    }),

    setWeekStart: assign({
      slotWeekStart: ({ event }): string | undefined => {
        const e = event as Extract<BookingEvent, { type: 'SET_WEEK_START' }>;
        return e.weekStart;
      },
    }),

    assignCustomerInfo: assign({
      customerInfo: ({ event }): BookingContext['customerInfo'] => {
        const e = event as Extract<BookingEvent, { type: 'SAVE_CUSTOMER_INFO' }>;
        return e.info;
      },
    }),

    assignPreReservation: assign({
      preReservationUuid: ({ event }): string | undefined => {
        const e = event as Extract<BookingEvent, { type: 'PRE_RESERVATION_CREATED' }>;
        return e.data.reservation_uuid;
      },
      preReservationData: ({ event }): PreReservationResponse | undefined => {
        const e = event as Extract<BookingEvent, { type: 'PRE_RESERVATION_CREATED' }>;
        return e.data;
      },
      // Les codes retenus par la pré-réservation font foi (un code refusé à la création n'y est plus).
      appliedCodes: ({ event }): AppliedCodes => {
        const e = event as Extract<BookingEvent, { type: 'PRE_RESERVATION_CREATED' }>;
        return {
          ...(e.data.promo_code ? { promo: e.data.promo_code } : {}),
          ...(e.data.voucher_code ? { voucher: e.data.voucher_code } : {}),
        };
      },
      confirmedReservationUuid: (): string | undefined => undefined,
    }),

    clearPreReservation: assign((): Partial<BookingContext> => PRE_RESERVATION_CLEARED),

    assignPaymentConfirmed: assign({
      dataLoading: (): boolean => false,
      confirmedReservationUuid: ({ event }): string | undefined => {
        const e = event as Extract<BookingEvent, { type: 'PAYMENT_CONFIRMED' }>;
        return e.reservationUuid;
      },
    }),

    /* Retour sur le choix du lieu : tout ce qui en dépend est à refaire, les coordonnées restent. */
    clearDownstreamFromPlace: assign(
      (): Partial<BookingContext> => ({
        selectedPlaceId: undefined,
        customerAddress: undefined,
        memberAssignments: [],
        resourceAssignments: [],
        ...SLOT_CLEARED,
      }),
    ),

    /* Retour à l'étape créneau depuis la finalisation : le créneau retenu reste affiché, la pré-réservation part. */
    backToSlot: assign((): Partial<BookingContext> => ({ slotNotice: undefined, ...PRE_RESERVATION_CLEARED })),

    /* Retour forcé à l'étape créneau, avec le bandeau qui dit pourquoi. */
    slotLost: assign(
      ({ event }): Partial<BookingContext> => ({
        ...SLOT_CLEARED,
        slotNotice: event.type === 'SLOT_TAKEN' ? 'taken' : 'expired',
      }),
    ),

    assignQuotePrestation: assign({
      quotePrestationId: ({ event }): number | undefined => {
        const e = event as Extract<BookingEvent, { type: 'REQUEST_QUOTE' }>;
        return e.prestationId;
      },
    }),

    clearQuotePrestation: assign({
      quotePrestationId: (): number | undefined => undefined,
    }),

    setError: assign({
      dataLoading: (): boolean => false,
      error: ({ event }): string | undefined => {
        if ('error' in event && typeof event.error === 'string') return event.error;
        return undefined;
      },
    }),

    resetForRetry: assign({
      dataLoading: (): boolean => true,
      error: (): string | undefined => undefined,
    }),
  },
}).createMachine({
  id: 'booking',
  initial: 'loading',
  context: initialContext,

  states: {
    /* ── Loading initial data ───────────────── */
    loading: {
      on: {
        DATA_LOADED: {
          actions: 'assignDataLoaded',
          target: 'services',
        },
        DATA_ERROR: {
          actions: 'setError',
          target: 'error',
        },
      },
    },

    /* ── Error state ────────────────────────── */
    /* Pas final : « Réessayer » relance le chargement (bug 22). */
    error: {
      on: {
        RETRY: {
          actions: 'resetForRetry',
          target: 'loading',
        },
      },
    },

    /* ── Step 1: Service selection ───────────── */
    services: {
      on: {
        SET_ENTRY_MODE: { actions: 'assignEntryMode' },
        ADD_TO_CART: { actions: ['addToCart', 'clearCartSummary'] },
        REPLACE_CART: { actions: ['replaceCart', 'clearCartSummary'] },
        RESTORE_CART: { actions: ['restoreCart', 'clearCartSummary'] },
        REMOVE_FROM_CART: { actions: ['removeFromCart', 'clearCartSummary'] },
        REQUEST_QUOTE: { actions: 'assignQuotePrestation', target: 'quote' },
        CONFIRM_SERVICES: [
          {
            guard: 'singlePlaceIsZone',
            actions: ['autoAssignSinglePlace'],
            target: 'zone_address',
          },
          {
            guard: 'singlePlace',
            actions: ['autoAssignSinglePlace', 'initSlotSelectors', 'clearSlotAndGrid'],
            target: 'slot',
          },
          /* Express : lieu proposé d'office, modifiable au-dessus du calendrier. */
          {
            guard: 'placeChips',
            actions: ['assignDefaultChipPlace', 'initSlotSelectors', 'clearSlotAndGrid'],
            target: 'slot',
          },
          {
            target: 'place',
          },
        ],
      },
    },

    /* ── Demande de devis (hors parcours) ───── */
    quote: {
      on: {
        BACK: {
          actions: 'clearQuotePrestation',
          target: 'services',
        },
      },
    },

    /* ── Step 2: Place selection ─────────────── */
    place: {
      on: {
        SELECT_PLACE: [
          {
            guard: 'eventPlaceIsZone',
            actions: 'assignPlace',
            target: 'zone_address',
          },
          {
            actions: ['assignPlace', 'initSlotSelectors', 'clearSlotAndGrid'],
            target: 'slot',
          },
        ],
        BACK: {
          actions: ['clearDownstreamFromPlace', 'leaveExpress'],
          target: 'services',
        },
      },
    },

    /* ── Step 2.5: Zone address (only when selected place is GEO_JSON) ─ */
    zone_address: {
      on: {
        SAVE_CUSTOMER_ADDRESS: {
          actions: ['assignCustomerAddress', 'initSlotSelectors', 'clearSlotAndGrid'],
          target: 'slot',
        },
        BACK: [
          /* Express : la zone avait été choisie parmi les puces du calendrier, on y revient. */
          {
            guard: 'placeChips',
            actions: ['assignDefaultChipPlace', 'initSlotSelectors', 'clearSlotAndGrid'],
            target: 'slot',
          },
          {
            guard: 'singlePlace',
            actions: ['clearCustomerAddress', 'leaveExpress'],
            target: 'services',
          },
          {
            actions: 'clearCustomerAddress',
            target: 'place',
          },
        ],
      },
    },

    /* ── Step 3: Slot selection (lieu express, membre et ressource au-dessus du calendrier) ── */
    slot: {
      on: {
        SLOTS_LOADING: { actions: 'setSlotsLoading' },
        SLOTS_LOADED: { actions: 'assignSlots' },
        SET_WEEK_START: { actions: ['setWeekStart', 'clearSlot'] },
        SELECT_SLOT: { actions: 'assignSlot' },
        SELECT_PLACE: [
          {
            guard: 'eventPlaceIsZone',
            actions: ['assignPlace', 'clearSlotAndGrid'],
            target: 'zone_address',
          },
          {
            actions: ['assignPlace', 'initSlotSelectors', 'clearSlotAndGrid'],
          },
        ],
        SET_MEMBERS: { actions: 'assignMembers' },
        SET_RESOURCES: { actions: 'assignResources' },
        CONFIRM_SLOT: {
          target: 'checkout',
        },
        BACK: [
          {
            guard: 'selectedPlaceIsZone',
            actions: 'clearSlotAndGrid',
            target: 'zone_address',
          },
          /* Express ou lieu unique : retour à la carte de la prestation. */
          {
            guard: 'isExpress',
            actions: ['clearDownstreamFromPlace', 'leaveExpress'],
            target: 'services',
          },
          {
            guard: 'singlePlace',
            actions: ['clearDownstreamFromPlace', 'leaveExpress'],
            target: 'services',
          },
          {
            actions: 'clearDownstreamFromPlace',
            target: 'place',
          },
        ],
      },
    },

    /* ── Step 4: Checkout ────────────────────── */
    checkout: {
      on: {
        SAVE_CUSTOMER_INFO: {
          actions: 'assignCustomerInfo',
        },
        PRE_RESERVATION_CREATED: {
          actions: 'assignPreReservation',
        },
        PRE_RESERVATION_RELEASED: {
          actions: 'clearPreReservation',
        },
        PAYMENT_CONFIRMED: {
          actions: 'assignPaymentConfirmed',
          target: 'success',
        },
        /* Toute sortie libère la pré-réservation tout de suite (DELETE par BookingContext, qui connaît l'hôte) :
           minuteur expiré — sauf paiement en cours —, retour, « modifier » du récap (EDIT_STEP), coordonnées à
           modifier (PRE_RESERVATION_RELEASED). Les codes posés restent : ils repartent avec la prochaine. */
        TIMER_EXPIRED: {
          actions: 'slotLost',
          target: 'slot',
        },
        SLOT_TAKEN: {
          actions: 'slotLost',
          target: 'slot',
        },
        BACK: {
          actions: 'backToSlot',
          target: 'slot',
        },
      },
    },

    /* ── Success (final) ─────────────────────── */
    success: {
      type: 'final',
    },
  },

  on: {
    /* Confirmation de paiement — applicable quel que soit l'état courant (retour de redirection 3DS/Klarna). */
    PAYMENT_CONFIRMED: {
      actions: 'assignPaymentConfirmed',
      target: '.success',
    },

    CATALOGUE_LOADED: {
      actions: 'assignCatalogue',
    },

    /* Recalcul live de la disponibilité — applicable dans n'importe quel état, sans transition. */
    BOOKABILITY_REFRESHED: {
      actions: 'mergeBookability',
    },

    /* Résumé du panier (calcul différé de 300 ms + réseau) : sa réponse arrive souvent APRÈS
       « Continuer ». Acceptée seulement à l'étape prestations, elle était jetée alors que le
       chargement restait levé — total bloqué sur « … » jusqu'à la fin du tunnel. Le panier ne change
       qu'à l'étape prestations, et BookingContext ignore la réponse d'un panier dépassé. */
    CART_SUMMARY_LOADING: {
      actions: 'setCartSummaryLoading',
    },
    CART_SUMMARY_LOADED: {
      actions: 'assignCartSummary',
    },
    CART_SUMMARY_FAILED: {
      actions: 'stopCartSummaryLoading',
    },
    /* Codes du champ unique : posables dès l'étape créneau, ils survivent aux retours en arrière. */
    CODES_CHANGED: {
      actions: 'assignCodes',
    },

    PRESELECTIONS_DROPPED: {
      actions: 'setDroppedPreselections',
    },

    /* Liens « modifier » du récap. La pré-réservation éventuelle est libérée par BookingContext. */
    EDIT_STEP: [
      {
        guard: ({ event }): boolean => event.type === 'EDIT_STEP' && event.step === 'services',
        actions: ['clearDownstreamFromPlace', 'leaveExpress'],
        target: '.services',
      },
      {
        guard: ({ context, event }): boolean =>
          event.type === 'EDIT_STEP' && event.step === 'place' && !usesPlaceChips(context),
        actions: 'clearDownstreamFromPlace',
        target: '.place',
      },
      {
        guard: ({ event }): boolean => event.type === 'EDIT_STEP' && event.step === 'zone_address',
        actions: 'clearSlotAndGrid',
        target: '.zone_address',
      },
      /* Lieu choisi par les puces (express) ou créneau : retour à l'étape créneau. */
      {
        guard: ({ event }): boolean => event.type === 'EDIT_STEP' && (event.step === 'slot' || event.step === 'place'),
        actions: 'backToSlot',
        target: '.slot',
      },
    ],
  },
});

// ── Step from machine state value ───────────────

export function stepFromState(stateValue: string): BookingStep | 'loading' | 'error' {
  if (typeof stateValue === 'string') {
    return stateValue as BookingStep | 'loading' | 'error';
  }
  return 'services';
}
